import { Button, Checkbox, Dropdown, Navbar, Spacer, Switch, Text, Tooltip } from '@nextui-org/react';
import { useTheme as useNextTheme } from 'next-themes';
import Link from 'next/link';
import {
  Bell,
  Flag,
  GitCommit,
  LogOut,
  MessageCircle,
  Moon,
  Server,
  Settings,
  Shuffle,
  Sun,
  Twitter
} from 'react-feather';

import { useLazyQuery, useQuery } from '@apollo/client';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { gql } from '../generated';
import useAuth from '../hooks/useAuth';
import { supabase } from '../supabase-client';
import { getMMDDYYDate, isDev } from '../utils/utils';
import { DropdownSearch } from './Search';

const FRONTEND_VERSION = '2.1.1';
const FEEDBACK_LINK = 'https://forms.gle/8qF4MDJNjR7B3bVu9';

// const GET_REPORTS_QUERY = gql(`
//   query getReports {
//     reports {
//       id
//       solved
//       description
//       identifier
//       created_at
//     }
//   }`);

export const GET_REPORTS_QUERY = gql(`
  query getSolvedReports($solved: Boolean!) {
    reports(solved: $solved) {
      id
      solved
      description
      identifier
      created_at
    }
  }`);

const GET_RANDOM_HADITH_QUERY = gql(`
  query getRandomHadith {
    randomHadith {
      id
      bookSection
      number
      chapter {
        number
      }
      book {
        id
      }
    }
  }
`);

export default function Nav() {
  const { setTheme } = useNextTheme();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [shouldHideOnScroll, setShouldHideOnScroll] = useState(false);
  const [expandAllGradings, setExpandAllGradings] = useState<any>(false);
  const [expandAllComments, setExpandAllComments] = useState<any>(false);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [onlyShowArabic, setOnlyShowArabic] = useState<any>(false);
  const [showSolvedReports, setShowSolvedReports] = useState<any>(false);

  const router = useRouter();
  const user = useAuth();

  const collapseItems = ['Search', 'Library', 'Donate', 'About Us', 'News', 'Twitter'];
  const collapseItemsLinks = ['/search', '/', '/donate', '/about', '/news', 'https://twitter.com/thaqalaynnet'];

  const [getRandomHadith, { data: randomHadithData }] = useLazyQuery(GET_RANDOM_HADITH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
    setShouldHideOnScroll(position > 100);
  };

  // get localstorage and set showSolvedReports
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const showSolvedReports = localStorage.getItem('showSolvedReports');
      if (showSolvedReports) setShowSolvedReports(showSolvedReports == 'true');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    if (typeof window !== 'undefined') {
      const expandAllGradings = localStorage.getItem('expandAllGradings');
      const expandAllComments = localStorage.getItem('expandAllComments');
      const onlyShowArabic = localStorage.getItem('onlyShowArabic');

      if (expandAllGradings) setExpandAllGradings(expandAllGradings == 'true');
      if (expandAllComments) setExpandAllComments(expandAllComments == 'true');
      if (onlyShowArabic) setOnlyShowArabic(onlyShowArabic == 'true');
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { data } = useQuery(GET_REPORTS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !user,
    variables: {
      solved: showSolvedReports ? true : false,
    },
  });

  const filteredReports = data?.reports;

  // console.log('Frontend version:', scrollPosition);
  return (
    <Navbar
      // we need to use a key here to force a re-render when the scroll position changes
      key={String(shouldHideOnScroll)}
      isBordered
      shouldHideOnScroll={shouldHideOnScroll}
      variant="floating"
      css={{
        background: 'transparent',
      }}
    >
      <Navbar.Toggle aria-label="navbar toggle" showIn="xs" />
      <Navbar.Brand>
        <Link aria-label="Thaqalayn Logo" href="/" scroll={false}>
          <Image
            alt="Thaqalayn shia hadith library logo"
            className="min-w-max"
            height={30}
            src="/scale.png"
            width={30}
          />
        </Link>
        <Spacer x={1} />
        <Text b color="inherit" hideIn="sm">
          <Link href="/" scroll={false}>
            Thaqalayn
          </Link>
        </Text>
      </Navbar.Brand>
      <Navbar.Content enableCursorHighlight activeColor="warning" hideIn="xs" variant="highlight">
        <Navbar.Item>
          <DropdownSearch />
        </Navbar.Item>
      </Navbar.Content>
      <Navbar.Content
        css={{
          '@xs': {
            w: '12%',
            jc: 'flex-end',
          },
        }}
      >
        <Navbar.Link aria-label="Donate" color="primary" hideIn="sm" href="/donate">
          <Button auto bordered className="!-p0" color={'success'} size={'xs'}>
            Donate
          </Button>
        </Navbar.Link>
        <Navbar.Link aria-label="advanced search" hideIn="xs" href="/search">
          Search
        </Navbar.Link>
        <Navbar.Link aria-label="News" hideIn="md" href="/news">
          News
        </Navbar.Link>{' '}
        {isDev() && (
          <Navbar.Link
            aria-label="Random hadith"
            hideIn="md"
            onClick={() => {
              getRandomHadith().then(({ data }) => {
                // console.log(data);
                router.push(
                  `/hadith/${data?.randomHadith?.book?.id}/${data?.randomHadith?.bookSection}/${data?.randomHadith?.chapter?.number}/${data?.randomHadith?.number}`
                );
              });
            }}
          >
            <Tooltip content="Random Hadith" placement="bottom">
              <Shuffle size={20} />
            </Tooltip>
          </Navbar.Link>
        )}
        <Navbar.Link aria-label="Twitter" hideIn="sm" href="https://twitter.com/ThaqalaynNet" target="_blank">
          <Twitter size={20} />
        </Navbar.Link>
        {user && (
          <Dropdown closeOnSelect={false} placement="bottom-right">
            <Navbar.Item>
              <Dropdown.Trigger>
                <Bell className={filteredReports?.length ? 'text-yellow-600' : ''} role="button" size={20} />
              </Dropdown.Trigger>
            </Navbar.Item>
            <Dropdown.Menu
              aria-label="Notifications"
              color="primary"
              css={{ $$dropdownMenuWidth: '400px' }}
              disabledKeys={['title']}
              title="Reports"
              onAction={(actionKey) => {
                if (filteredReports) router.push(`/hadith/${filteredReports[(actionKey as number) - 1]?.identifier}`);
              }}
            >
              {([{}, ...(filteredReports ?? [])] || []).map((report, i) =>
                i == 0 ? (
                  <Dropdown.Section key={i}>
                    <Dropdown.Item key="title">
                      <div className="h-full w-full text-center flex flex-row">
                        <Flag className="inline m-auto text-red-300" size={20} />
                        <div className="ml-2 inline font-semibold">Reports</div>
                        <div className="inline ml-auto font-light">solved:</div>
                        <Switch
                          checked={showSolvedReports}
                          className="inline"
                          title="hi"
                          onClick={() => {
                            setShowSolvedReports(!showSolvedReports);
                            localStorage.setItem('showSolvedReports', String(!showSolvedReports));
                          }}
                        />
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Section>
                ) : (
                  <Dropdown.Section key={i}>
                    <Dropdown.Item key={i} aria-label="Notification" className="h-full" color="primary">
                      <div className="h-full flex flex-row">
                        <Checkbox
                          className="mr-1"
                          isSelected={!!report?.solved}
                          onClick={(e) => {
                            // stop propagation so that the dropdown doesn't close
                            e.stopPropagation();
                          }}
                        />
                        <div
                          className={`inline h-full px-1 flex-grow dark:text-yellow-50 text-slate-600 ${report?.solved && 'line-through'
                            }`}
                        >
                          {report?.description}
                        </div>
                        <div className="text-clip overflow-hidden inline min-w-min my-auto dark:text-indigo-300 text-violet-500">
                          {getMMDDYYDate(report?.created_at as string)}
                        </div>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Section>
                )
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {user && (
          <Navbar.Link
            aria-label="logout"
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
          >
            <Tooltip content="Logout" placement="bottom">
              <LogOut size={20} />
            </Tooltip>
          </Navbar.Link>
        )}
        <Dropdown placement="bottom-right">
          <Navbar.Item>
            <Dropdown.Trigger>
              <Settings role="button" size={20} />
            </Dropdown.Trigger>
          </Navbar.Item>
          <Dropdown.Menu
            aria-label="User menu actions"
            color="primary"
            onAction={(actionKey) => {
              // We need to delay the action to avoid the dropdown closing before the action is triggered
              setTimeout(() => {
                if (actionKey === 'dark') {
                  setTheme('dark');
                } else if (actionKey === 'light') {
                  setTheme('light');
                } else if (actionKey === 'system') {
                  setTheme('system');
                } else if (actionKey === 'feedback') {
                  router.push(FEEDBACK_LINK);
                } else if (actionKey === 'grading') {
                  setExpandAllGradings(!expandAllGradings);
                  localStorage.setItem('expandAllGradings', String(!expandAllGradings));
                  window.dispatchEvent(new Event('storage'));
                } else if (actionKey == 'comment') {
                  setExpandAllComments(!expandAllComments);
                  localStorage.setItem('expandAllComments', String(!expandAllComments));
                  window.dispatchEvent(new Event('storage'));
                } else if (actionKey === 'arabic') {
                  setOnlyShowArabic(!onlyShowArabic);
                  localStorage.setItem('onlyShowArabic', String(!onlyShowArabic));
                  window.dispatchEvent(new Event('storage'));
                }
              }, 100);
            }}
          >
            <Dropdown.Section title="Theme">
              <Dropdown.Item key="light">
                <Text color="inherit" css={{ d: 'flex' }}>
                  <Sun className="my-auto mr-2" size={15} /> Light
                </Text>
              </Dropdown.Item>
              <Dropdown.Item key="dark">
                <Text color="inherit" css={{ d: 'flex' }}>
                  <Moon className="my-auto mr-2" size={15} />
                  Dark
                </Text>
              </Dropdown.Item>
              <Dropdown.Item key="system">
                <Text color="inherit" css={{ d: 'flex' }}>
                  <Server className="my-auto mr-2" size={15} />
                  System
                </Text>
              </Dropdown.Item>
            </Dropdown.Section>
            <Dropdown.Section title="Settings">
              <Dropdown.Item key="grading">
                <div className="flex flex-row">
                  <Switch checked={expandAllGradings} className="inline my-auto !p-0" size="sm" />
                  <div className="inline text-sm my-auto ml-2 p-0">Always show gradings</div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item key="comment">
                <div className="flex flex-row">
                  <Switch checked={expandAllComments} className="inline my-auto !p-0" size="sm" />
                  <div className="inline text-sm my-auto ml-2 p-0">Always show comments</div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item key="arabic">
                <div className="flex flex-row">
                  <Switch checked={onlyShowArabic} className="inline my-auto !p-0" size="sm" />
                  <div className="inline text-sm my-auto ml-2 p-0">Only show Arabic</div>
                </div>
              </Dropdown.Item>
            </Dropdown.Section>
            <Dropdown.Section>
              <Dropdown.Item key="feedback">
                <Link href={FEEDBACK_LINK}>
                  <Text color="inherit" css={{ d: 'flex' }}>
                    <MessageCircle className="my-auto mr-2" size={15} />
                    Feedback
                  </Text>
                </Link>
              </Dropdown.Item>
            </Dropdown.Section>
            <Dropdown.Section title="Version">
              <Dropdown.Item key="version">
                <Text color="inherit" css={{ d: 'flex' }}>
                  <GitCommit className="my-auto mr-2" size={15} /> {FRONTEND_VERSION}
                </Text>
              </Dropdown.Item>
            </Dropdown.Section>
          </Dropdown.Menu>
        </Dropdown>
      </Navbar.Content>
      <Navbar.Collapse disableAnimation aria-label="Navigation menu">
        {collapseItems.map((item, index) => (
          <Navbar.CollapseItem
            key={item}
          // activeColor="warning"
          // css={{
          //   color: index === collapseItems.length - 1 ? '$error' : '',
          // }}
          // isActive={index === 2}
          >
            <Link color="inherit" href={collapseItemsLinks[index]}>
              {item}
            </Link>
          </Navbar.CollapseItem>
        ))}
      </Navbar.Collapse>
    </Navbar>
  );
}
